import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";

import { DeviceEnum } from "../../types/context-detection";
import { DeviceScrollEnum } from "../../types/suggestions";
import { IHandleKeyboardNavigation } from "../../types/keyboard-navigation";
import { IPersonalizedSuggestions, ISuggestionResponse } from "../../types/suggestion-response";
import { SearchBoxProps } from "../../types/searchbox";

import debounce from "../../util/debounce";
import useSuggestionsRenderView from "../../util/suggestions/suggestions-render-view";
import { defaultSuggestionResponse } from "../../util/suggestions/defaultSuggestionResponse";
import { L10nContext, NavigationContext } from "../../util/contexts";
import { PageViewLogDataId } from "../../util/pageViewLogDataId";
import { sendSuggestionsEventToKraken } from "../../util/events/kraken";
import { setStoredSearchQuery } from "../../util/queryStorage";
import { subscribeToPriceMode, PriceMode, getCurrentPriceMode } from "../../util/priceModeObserver";
import { useKeyboardNavigation } from "../../util/suggestions/keyboard-navigation";
import { useL10n } from "../../util/l10n";
import { usePersonalizedSuggestions } from "../../util/personalization/usePersonalizedSuggestions";
import {
    getCurrentZeroQueryOverwrite,
    subscribeToAbTestMode,
    ZeroQueryOverwriteFeature
} from "../../util/personalization/abTestObserver";

import PersonalizedSuggestionColumns from "../suggestion-list/personalized-suggestion-columns";
import PersonalizedSuggestionList from "../suggestion-list/personalized-suggestion-list";
import SearchIcon from "../../Assets/svg/Search_2021_icon";
import SuggestionColumns from "../suggestion-list/suggestion-columns";
import SuggestionList from "../suggestion-list/suggestion-list";

import "./searchbox-flyout.scss";
import "../suggestion-list/suggestions-flyout.scss";

const SearchBoxFlyout = ({ detectedContext, ...props }: SearchBoxProps) => {
    const { isWebShop, isMobileShop, device, customerType } = detectedContext;
    const { personalizedData, isLoading, isEnabled } = usePersonalizedSuggestions();
    const SEARCH_TERM_LENGTH_LIMIT = 50;
    const TOP_SEARCH_QUERIES_LIMIT = isWebShop ? 10 : 5;
    const showColumns = useSuggestionsRenderView("flyout", isMobileShop);

    const [isOpen, setIsOpen] = useState(false);
    const [focus, setFocus] = useState(false);
    const [input, setInput] = useState("");
    const [priceMode, setPriceMode] = useState<PriceMode>("gross");
    const [validSearchTerm, setValidSearchTerm] = useState("");
    const [hasValidSearchTerm, setHasValidSearchTerm] = useState(false);

    const [isPersonalizationAvailable, setIsPersonalizationAvailable] = useState(false);

    // for ShopSearchEnableZeroQuerySuggestions off:
    const [hasSuggestions, setHasSuggestions] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState<ISuggestionResponse>(
        defaultSuggestionResponse()
    );
    const keyboardNavColumnElementRef = useRef<IHandleKeyboardNavigation | null>(null);
    const keyboardNavListElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    // for ShopSearchEnableZeroQuerySuggestions on:
    const [recoViewedEventSent, setRecoViewedEventSent] = useState(false);
    const [hasPersonalizedSuggestions, setHasPersonalizedSuggestions] = useState(false);
    const [showPersonalizedSuggestions, setShowPersonalizedSuggestions] = useState(false);
    const [personalizedSuggestions, setPersonalizedSuggestions] =
        useState<IPersonalizedSuggestions>(personalizedData);
    const keyboardNavPersonalizedColumnElementRef = useRef<IHandleKeyboardNavigation | null>(null);
    const keyboardNavPersonalizedListElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    const [zeroQueryOverwrite, setZeroQueryOverwrite] =
        useState<ZeroQueryOverwriteFeature>("enabled");

    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusRef = useRef(focus);
    const suggestionsRef = useRef(suggestions);
    const hasValidSearchTermRef = useRef(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const pageViewLogDataId = PageViewLogDataId.get();

    const l10n = useL10n(props.l10n);

    useEffect(() => {
        focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
        const isValidSearchTerm = validSearchTerm !== "";
        hasValidSearchTermRef.current = isValidSearchTerm;
        setHasValidSearchTerm(isValidSearchTerm);
    }, [validSearchTerm]);

    useEffect(() => {
        const isAvailable = !isLoading && isEnabled && zeroQueryOverwrite === "enabled";
        setPersonalizedSuggestions({ ...personalizedData });
        setIsPersonalizationAvailable(isAvailable);
    }, [isLoading, isEnabled, zeroQueryOverwrite, personalizedData]);

    useEffect(() => {
        const isValidSearchWithoutSuggestions = !hasPersonalizedSuggestions && hasValidSearchTerm;
        const shouldBeShown =
            isPersonalizationAvailable && isOpen && !isValidSearchWithoutSuggestions;
        setShowPersonalizedSuggestions(shouldBeShown);
    }, [isPersonalizationAvailable, hasPersonalizedSuggestions, hasValidSearchTerm, isOpen]);

    useEffect(() => {
        if (showPersonalizedSuggestions) {
            setRecoViewedEventSent(true);
        }
    }, [showPersonalizedSuggestions]);

    useEffect(() => {
        setPriceMode(getCurrentPriceMode());
        const unsubscribe = subscribeToPriceMode((priceMode) => setPriceMode(priceMode));
        return unsubscribe;
    }, []);

    useEffect(() => {
        // ESPP2-5062
        const [portalSegment] = window.location.pathname.split("/").filter(Boolean);
        if (portalSegment === "uk") {
            setZeroQueryOverwrite(getCurrentZeroQueryOverwrite());
            const unsubscribe = subscribeToAbTestMode((overwrite) =>
                setZeroQueryOverwrite(overwrite)
            );
            return unsubscribe;
        }
    }, []);

    useEffect(() => {
        suggestionsRef.current = suggestions;
    }, [suggestions]);

    useEffect(() => {
        const hasSuggestions =
            suggestions.articles.length > 0 ||
            suggestions.flexibleSets.length > 0 ||
            suggestions.contents.length > 0 ||
            suggestions.topSearchQueries.length > 0;
        setHasSuggestions(hasSuggestions);
    }, [suggestions]);

    useEffect(() => {
        const hasPersonalizedData =
            personalizedSuggestions.articles.length > 0 ||
            personalizedSuggestions.flexibleSets.length > 0 ||
            personalizedSuggestions.contents.length > 0 ||
            personalizedSuggestions.topSearchQueries.length > 0 ||
            personalizedSuggestions.storedQueries.length > 0;
        setHasPersonalizedSuggestions(hasPersonalizedData);
    }, [personalizedSuggestions]);

    useEffect(() => {
        const shouldBeShown = hasSuggestions && isOpen && hasValidSearchTerm;
        setShowSuggestions(shouldBeShown);
    }, [hasSuggestions, isOpen, hasValidSearchTerm]);

    const handleClick = useCallback((event: Event) => {
        const target = event.target as HTMLElement;

        // exclude clicks on searchbox and on mkt header
        const removeFocus =
            focusRef.current &&
            !searchBoxRef.current?.contains(target) &&
            !target.closest(".mkt-headr-content");

        if (removeFocus) {
            handleBlur();
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);

    useEffect(() => {
        shell?.messageBroker.subscribeTo(
            "ESMKT.HeadR.FlyoutOpened",
            (data: unknown) => {
                const payload = data as string;
                if (payload === "SearchBoxFlyout") onFlyoutOpened();
            },
            "ESMKT.HeadR.FlyoutOpened"
        );

        shell?.messageBroker.subscribeTo(
            "ESMKT.HeadR.FlyoutClosed",
            (data: unknown) => {
                const payload = data as string;
                if (payload === "SearchBoxFlyout") onFlyoutClosed();
            },
            "ESMKT.HeadR.FlyoutClosed"
        );

        return () => {
            shell?.messageBroker.unsubscribeFrom(
                "ESMKT.HeadR.FlyoutOpened",
                "ESMKT.HeadR.FlyoutOpened"
            );
            shell?.messageBroker.unsubscribeFrom(
                "ESMKT.HeadR.FlyoutClosed",
                "ESMKT.HeadR.FlyoutClosed"
            );
        };
    }, []);

    const toggleFlyoutTriggeredByTabNavigation = useCallback((event: KeyboardEvent) => {
        if (event.key === "Enter") {
            shell?.messageBroker.publishTo("ESMKT.HeadR.ToggleFlyout", "SearchBoxFlyout");
        } else if (event.shiftKey && event.key === "Tab") {
            closeFlyoutTriggeredByTabNavigation();
        }
    }, []);

    useEffect(() => {
        const iconWrapper = document.querySelector<HTMLElement>(
            ".scope-ssw.mkt-headr-icon-wrapper"
        );
        const flyoutIcon = iconWrapper?.querySelector<HTMLElement>(".ssw-flyout-icon");
        if (flyoutIcon) {
            flyoutIcon.addEventListener("keydown", toggleFlyoutTriggeredByTabNavigation);

            return () => {
                flyoutIcon.removeEventListener("keydown", toggleFlyoutTriggeredByTabNavigation);
            };
        }
    }, [toggleFlyoutTriggeredByTabNavigation]);

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement> | KeyboardEvent) => {
            e.preventDefault();

            const trimmedInput = input.trim();
            if (trimmedInput) {
                const target = `${props.searchTarget}?query=${encodeURIComponent(trimmedInput)}`;
                if (isPersonalizationAvailable)
                    setStoredSearchQuery({ name: trimmedInput, target });
                window.location.href = target;
            }
        },
        [input, props.searchTarget, isPersonalizationAvailable]
    );

    const personalizedNavApi = useKeyboardNavigation(
        showColumns
            ? () => keyboardNavPersonalizedColumnElementRef.current?.getNavigationElements() ?? []
            : () => keyboardNavPersonalizedListElementRef.current?.getNavigationElements() ?? []
    );

    const navigationApi = useKeyboardNavigation(
        showColumns
            ? () => keyboardNavColumnElementRef.current?.getNavigationElements() ?? []
            : () => keyboardNavListElementRef.current?.getNavigationElements() ?? []
    );

    const handleKeyPressedWhileFocused = useCallback(
        (event: KeyboardEvent) => {
            const navApi = isPersonalizationAvailable ? personalizedNavApi : navigationApi;
            const activeNavRef = isPersonalizationAvailable
                ? showColumns
                    ? keyboardNavPersonalizedColumnElementRef
                    : keyboardNavPersonalizedListElementRef
                : showColumns
                  ? keyboardNavColumnElementRef
                  : keyboardNavListElementRef;

            if (event.key === "Tab" || (event.shiftKey && event.key === "Tab")) {
                closeFlyoutTriggeredByTabNavigation();
            } else if (
                (event.key === "Escape" || event.key === "Esc") &&
                activeNavRef.current !== null
            ) {
                shell?.tabNav.focusPrevious();
                closeFlyoutTriggeredByTabNavigation();
            } else if (event.key === "Enter" && !navApi.hasSelectedNavElement()) {
                handleSubmit(event);
            } else if (device === DeviceEnum.Desktop) {
                navApi.handleKeyDown(event);
            }
        },
        [
            isPersonalizationAvailable,
            showColumns,
            device,
            navigationApi,
            personalizedNavApi,
            handleSubmit
        ]
    );

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        if (focus) {
            input.addEventListener("keydown", handleKeyPressedWhileFocused);
        } else {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        }

        return () => {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        };
    }, [focus, handleKeyPressedWhileFocused]);

    useEffect(() => {
        const suggestionsVisible = hasSuggestions && showSuggestions;
        const hasElements = showColumns
            ? keyboardNavColumnElementRef.current?.hasNavigationElements()
            : keyboardNavListElementRef.current?.hasNavigationElements();
        if (!suggestionsVisible && hasElements) {
            navigationApi.resetNavigation();
        }
    }, [hasSuggestions, showSuggestions, showColumns, navigationApi]);

    useEffect(() => {
        const suggestionsVisible = hasPersonalizedSuggestions && showPersonalizedSuggestions;
        const hasElements = showColumns
            ? keyboardNavPersonalizedColumnElementRef.current?.hasNavigationElements()
            : keyboardNavPersonalizedListElementRef.current?.hasNavigationElements();
        if (!suggestionsVisible && hasElements) {
            personalizedNavApi.resetNavigation();
        }
    }, [hasPersonalizedSuggestions, showPersonalizedSuggestions, showColumns, personalizedNavApi]);

    const handleBlur = () => {
        setFocus(false);
    };

    function onFlyoutOpened(): void {
        setIsOpen(true);
        inputRef.current?.toggleAttribute("focusable");

        // might need adjustment for desktop vs isTouchDevice
        setFocus(true);
        inputRef.current?.focus();
    }

    function onFlyoutClosed(): void {
        setIsOpen(false);
        inputRef.current?.toggleAttribute("focusable");

        // might need adjustment for desktop vs isTouchDevice
        setFocus(false);
    }

    function handleOnFocusFallback(event: React.FocusEvent<HTMLInputElement>): void {
        const refocusAfterLostFocus =
            event.target?.classList.contains("ssw-input") && !event.relatedTarget;

        setTimeout(() => {
            if (!focusRef.current && refocusAfterLostFocus) {
                // will only execute if the focus was lost to an unexpected action
                setFocus(true);
            }
        }, 500);
    }

    function handleOnBlurFallback(): void {
        setTimeout(() => {
            if (focusRef.current) {
                // will only execute when the input lost focus, but the flyout was not hidden
                setFocus(false);
            }
        }, 500);
    }

    function closeFlyoutTriggeredByTabNavigation(): void {
        shell?.messageBroker.publishTo("ESMKT.HeadR.CloseFlyout", null);
    }

    const sanatizeInput = (input: string) => {
        const validInput = input.length > SEARCH_TERM_LENGTH_LIMIT ? input.slice(0, 50) : input;
        return validInput;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? "";
        const sanatizedInput = sanatizeInput(value);
        setInput(sanatizedInput);

        const trimmedInput = sanatizedInput.trim();
        if (trimmedInput === "") {
            setValidSearchTerm("");
            debouncedFetch.current.cancel();
            resetSuggestionResponse();
            resetPersonalizedSuggestionResponse();
        } else {
            setValidSearchTerm(trimmedInput);
            debouncedFetch.current(trimmedInput);
        }
    };

    const debouncedFetch = useRef<((input: string) => void) & { cancel: () => void }>(
        debounce((input: string) => {
            fetchSuggestions(input);
        }, 250)
    );

    const fetchSuggestions = async (input: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const headers: Record<string, string> = pageViewLogDataId
            ? { "X-PageView-ID": pageViewLogDataId }
            : {};

        try {
            const res = await fetch(
                `${props.apiUrl}suggestion/?searchTerm=${encodeURIComponent(
                    input
                )}&topSearchQueriesLimit=${TOP_SEARCH_QUERIES_LIMIT}`,
                { headers, signal: controller.signal }
            );

            if (!res.ok) throw new Error("Network response was not ok");

            const response = await res.json();

            if (
                response.articles.length ||
                response.flexibleSets.length ||
                response.contents.length ||
                response.topSearchQueries.length
            ) {
                const newSuggestions = response as ISuggestionResponse;

                const handleEmptySearchTermEdgeCase = !hasValidSearchTermRef.current;
                if (handleEmptySearchTermEdgeCase) return;

                if (JSON.stringify(newSuggestions) !== JSON.stringify(suggestionsRef.current)) {
                    sendSuggestionsEventToKraken(input);
                }

                setSuggestions(newSuggestions);
                setPersonalizedSuggestions({
                    ...newSuggestions,
                    isSuggestionResponse: true,
                    storedQueries: []
                });
            } else {
                resetSuggestionResponse();
                resetPersonalizedSuggestionResponse();
            }
        } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
                resetSuggestionResponse();
                resetPersonalizedSuggestionResponse();
            }
        } finally {
            abortControllerRef.current = null;
        }
    };

    const resetSuggestionResponse = () => {
        setSuggestions(defaultSuggestionResponse());
    };

    const resetPersonalizedSuggestionResponse = () => {
        setPersonalizedSuggestions({ ...personalizedData });
    };

    return (
        <L10nContext.Provider value={l10n}>
            <div
                className={
                    "searchbox flyout" +
                    ` ssw-current-pricemode-${priceMode}` +
                    ` ${isOpen ? "open" : ""}` +
                    ` ${focus ? "focus" : ""}` +
                    ` ${props.tenant}`
                }
                ref={searchBoxRef}
            >
                <div className="search">
                    <form
                        method="get"
                        action={props.searchTarget}
                        acceptCharset="utf-8"
                        onSubmit={handleSubmit}
                    >
                        <input
                            ref={inputRef}
                            value={input}
                            maxLength={50}
                            onChange={handleChange}
                            onFocus={handleOnFocusFallback}
                            onBlur={handleOnBlurFallback}
                            className="ssw-input"
                            type="text"
                            name="query"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            aria-label={l10n.defaultTerm}
                        />
                        <button className="ssw-button" type="submit" aria-label={l10n.defaultTerm}>
                            <SearchIcon />
                        </button>
                    </form>
                </div>

                {isPersonalizationAvailable
                    ? showPersonalizedSuggestions && (
                          <div
                              id={`new-suggestions-${props.view}`}
                              className={"new-suggestions merged-personalized"}
                          >
                              {showColumns ? (
                                  <NavigationContext.Provider value={personalizedNavApi}>
                                      <PersonalizedSuggestionColumns
                                          navigationRef={keyboardNavPersonalizedColumnElementRef}
                                          isPersonalizedData={
                                              !personalizedSuggestions.isSuggestionResponse
                                          }
                                          portal={props.portal}
                                          culture={props.culture}
                                          searchTarget={props.searchTarget}
                                          trackingEndpoint={props.trackingEndpoint}
                                          articles={personalizedSuggestions.articles}
                                          flexibleSets={personalizedSuggestions.flexibleSets}
                                          storedQueries={personalizedSuggestions.storedQueries}
                                          contents={personalizedSuggestions.contents}
                                          featurePromotion={
                                              personalizedSuggestions.featurePromotion
                                          }
                                          topSearchQueries={
                                              personalizedSuggestions.topSearchQueries
                                          }
                                          priceMandantSettings={
                                              personalizedSuggestions.priceMandantSettingsArticles
                                          }
                                          priceMandantSettingsV2={
                                              personalizedSuggestions.priceMandantSettingsFlexibleSet
                                          }
                                          customerType={customerType}
                                          searchTerm={validSearchTerm}
                                          isMobileShop={isMobileShop}
                                          view={props.view}
                                          pageViewLogDataId={pageViewLogDataId}
                                          deviceScrollClass={DeviceScrollEnum.None}
                                          deviceType={device}
                                          recoViewedEventSent={recoViewedEventSent}
                                      />
                                  </NavigationContext.Provider>
                              ) : (
                                  <NavigationContext.Provider value={personalizedNavApi}>
                                      <PersonalizedSuggestionList
                                          navigationRef={keyboardNavPersonalizedListElementRef}
                                          isPersonalizedData={
                                              !personalizedSuggestions.isSuggestionResponse
                                          }
                                          portal={props.portal}
                                          culture={props.culture}
                                          searchTarget={props.searchTarget}
                                          trackingEndpoint={props.trackingEndpoint}
                                          articles={personalizedSuggestions.articles}
                                          flexibleSets={personalizedSuggestions.flexibleSets}
                                          storedQueries={personalizedSuggestions.storedQueries}
                                          contents={personalizedSuggestions.contents}
                                          featurePromotion={
                                              personalizedSuggestions.featurePromotion
                                          }
                                          topSearchQueries={
                                              personalizedSuggestions.topSearchQueries
                                          }
                                          priceMandantSettings={
                                              personalizedSuggestions.priceMandantSettingsArticles
                                          }
                                          priceMandantSettingsV2={
                                              personalizedSuggestions.priceMandantSettingsFlexibleSet
                                          }
                                          customerType={customerType}
                                          searchTerm={validSearchTerm}
                                          isMobileShop={isMobileShop}
                                          view={props.view}
                                          pageViewLogDataId={pageViewLogDataId}
                                          deviceScrollClass={DeviceScrollEnum.None}
                                          deviceType={device}
                                          recoViewedEventSent={recoViewedEventSent}
                                      />
                                  </NavigationContext.Provider>
                              )}
                          </div>
                      )
                    : showSuggestions && (
                          <div id={`new-suggestions-${props.view}`} className={"new-suggestions"}>
                              {showColumns ? (
                                  <NavigationContext.Provider value={navigationApi}>
                                      <SuggestionColumns
                                          navigationRef={keyboardNavColumnElementRef}
                                          portal={props.portal}
                                          culture={props.culture}
                                          searchTarget={props.searchTarget}
                                          trackingEndpoint={props.trackingEndpoint}
                                          articles={suggestions.articles}
                                          flexibleSets={suggestions.flexibleSets}
                                          contents={suggestions.contents}
                                          featurePromotion={suggestions.featurePromotion}
                                          topSearchQueries={suggestions.topSearchQueries}
                                          priceMandantSettings={
                                              suggestions.priceMandantSettingsArticles
                                          }
                                          priceMandantSettingsV2={
                                              suggestions.priceMandantSettingsFlexibleSet
                                          }
                                          customerType={customerType}
                                          searchTerm={validSearchTerm}
                                          isMobileShop={isMobileShop}
                                          view={props.view}
                                          pageViewLogDataId={pageViewLogDataId}
                                          deviceScrollClass={DeviceScrollEnum.None}
                                          deviceType={device}
                                      />
                                  </NavigationContext.Provider>
                              ) : (
                                  <NavigationContext.Provider value={navigationApi}>
                                      <SuggestionList
                                          navigationRef={keyboardNavListElementRef}
                                          portal={props.portal}
                                          culture={props.culture}
                                          searchTarget={props.searchTarget}
                                          trackingEndpoint={props.trackingEndpoint}
                                          articles={suggestions.articles}
                                          flexibleSets={suggestions.flexibleSets}
                                          contents={suggestions.contents}
                                          featurePromotion={suggestions.featurePromotion}
                                          topSearchQueries={suggestions.topSearchQueries}
                                          priceMandantSettings={
                                              suggestions.priceMandantSettingsArticles
                                          }
                                          priceMandantSettingsV2={
                                              suggestions.priceMandantSettingsFlexibleSet
                                          }
                                          customerType={customerType}
                                          searchTerm={validSearchTerm}
                                          isMobileShop={isMobileShop}
                                          view={props.view}
                                          pageViewLogDataId={pageViewLogDataId}
                                          deviceType={device}
                                      />
                                  </NavigationContext.Provider>
                              )}
                          </div>
                      )}
            </div>
        </L10nContext.Provider>
    );
};

export default SearchBoxFlyout;
